import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import logger from '../utils/logger';
import { Calendar, ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import { OnboardingCalendar, STEPS, getStepStyle } from './onboarding/OnboardingCalendar';
import { ScheduleStep, PrivacyStep, ReviewStep } from './onboarding/OnboardingSteps';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENCY_SYMBOLS = { NGN: '\u20A6', GBP: '\u00A3', EUR: '\u20AC' };

export default function AssistedOnboardingModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(0);
  const [fee, setFee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [maskedMode, setMaskedMode] = useState(true);
  const [notes, setNotes] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [dateError, setDateError] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const token = localStorage.getItem('auth_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const currencySymbol = CURRENCY_SYMBOLS[fee?.fee_currency] || '$';

  useEffect(() => {
    if (isOpen) {
      fetch(`${BACKEND_URL}/api/assisted-onboarding/fee`, { headers, cache: 'no-store' }).then(r => r.json()).then(setFee).catch(() => {});
      setStep(0); setSelectedDate(''); setSelectedTime(''); setMaskedMode(true); setNotes('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadSlots = useCallback(async (dateStr) => {
    setSlotsLoading(true); setDateError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/available-slots?date_str=${dateStr}`, { headers, cache: 'no-store' });
      const data = await res.json();
      if (!data.available) { setDateError(data.reason); setSlots([]); } else { setSlots(data.slots); }
    } catch (err) { logger.error(err); setDateError('Failed to load slots'); } finally { setSlotsLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateClick = (dateStr) => { setSelectedDate(dateStr); setSelectedTime(''); loadSlots(dateStr); };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/request`, {
        method: 'POST', headers,
        body: JSON.stringify({ scheduled_date: selectedDate, scheduled_time: selectedTime, masked_mode: maskedMode, notes: notes || null }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Request failed'); }
      const session = await res.json();
      onSuccess?.(session); onClose();
    } catch (err) { alert(err.message); } finally { setSubmitting(false); }
  };

  const calendarComponent = <OnboardingCalendar calendarMonth={calendarMonth} setCalendarMonth={setCalendarMonth} selectedDate={selectedDate} onDateClick={handleDateClick} />;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="assisted-onboarding-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-600" />Request Virtual Assisted Onboarding</DialogTitle>
          <DialogDescription className="text-sm text-slate-500 mt-1">Book a 60-minute guided session with a MIF Support specialist</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 px-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-medium ${i <= step ? 'text-emerald-600' : 'text-slate-300'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${getStepStyle(i, step)}`}>
                  {i < step ? <Check className="w-3 h-3" /> : i + 1}
                </div>
                <span className="capitalize hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? 'bg-emerald-600' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>
        <div className="min-h-[280px]">
          {step === 0 && <ScheduleStep fee={fee} currencySymbol={currencySymbol} calendarComponent={calendarComponent} dateError={dateError} selectedDate={selectedDate} slots={slots} slotsLoading={slotsLoading} selectedTime={selectedTime} setSelectedTime={setSelectedTime} />}
          {step === 1 && <PrivacyStep maskedMode={maskedMode} setMaskedMode={setMaskedMode} notes={notes} setNotes={setNotes} />}
          {step === 2 && <ReviewStep selectedDate={selectedDate} selectedTime={selectedTime} maskedMode={maskedMode} notes={notes} currencySymbol={currencySymbol} fee={fee} />}
        </div>
        <DialogFooter className="flex gap-2">
          {step > 0 && <Button variant="outline" onClick={() => setStep(s => s - 1)} data-testid="onboarding-back-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}
          <div className="flex-1" />
          {step < 2 ? (
            <Button onClick={() => setStep(s => s + 1)} disabled={step === 0 && (!selectedDate || !selectedTime)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="onboarding-next-btn">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="onboarding-submit-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Submit Request</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
